import { isMockServices } from 'src/utils';
import { getCurrentApiUrl } from 'src/utils/apiUrlResolver';

export interface ApiHealthStatus {
  status: 'connected' | 'offline' | 'checking';
  message: string;
  responseTime?: number;
  lastChecked: Date;
}

/**
 * Check MOS API health by attempting to connect to the health endpoint
 */
export const checkApiHealth = async (): Promise<ApiHealthStatus> => {
  const startTime = Date.now();
  const lastChecked = new Date();

  try {
    // If mock services are enabled, return a mock successful response
    if (isMockServices()) {
      return {
        status: 'connected',
        message: 'Mock API - Connected',
        responseTime: 50,
        lastChecked,
      };
    }

    const apiUrl = getCurrentApiUrl();

    if (!apiUrl) {
      return {
        status: 'offline',
        message: 'API URL not configured',
        lastChecked,
      };
    }

    // Try to reach the API health endpoint with a timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

    try {
      const response = await fetch(`${apiUrl}/health`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      const responseTime = Date.now() - startTime;

      if (response.ok) {
        return {
          status: 'connected',
          message: 'API Connected',
          responseTime,
          lastChecked,
        };
      } else {
        return {
          status: 'offline',
          message: `API Error: ${response.status} ${response.statusText}`,
          responseTime,
          lastChecked,
        };
      }
    } catch (fetchError) {
      clearTimeout(timeoutId);

      if (fetchError instanceof Error) {
        if (fetchError.name === 'AbortError') {
          return {
            status: 'offline',
            message: 'API Timeout (10s)',
            responseTime: Date.now() - startTime,
            lastChecked,
          };
        }

        // Network or other fetch errors
        return {
          status: 'offline',
          message: `Connection Failed: ${fetchError.message}`,
          responseTime: Date.now() - startTime,
          lastChecked,
        };
      }

      return {
        status: 'offline',
        message: 'Unknown connection error',
        responseTime: Date.now() - startTime,
        lastChecked,
      };
    }
  } catch (error) {
    return {
      status: 'offline',
      message: error instanceof Error ? error.message : 'Health check failed',
      responseTime: Date.now() - startTime,
      lastChecked,
    };
  }
};

export default checkApiHealth;
